import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { WorkflowContextType } from '../../4-module/workflowCompletion/workflowCompletion';

const CANCEL_MANUAL_PROCESS = gql`
  mutation($input: TransitionManualProcessInput!) {
    transitionManualProcess(input: $input) {
      manualProcess {
        id
      }
    }
  }
`;

const CANCEL_CUSTODIAN_DEPOSIT_TRANSFER = gql`
  mutation($input: CancelCustodianDepositTransferInput!) {
    cancelCustodianDepositTransfer(input: $input) {
      confirmationNumber
    }
  }
`;

const DELETE_TRANSFERS = gql`
  mutation($input: TransitionTransferInput!) {
    transitionTransfer(input: $input) {
      transfer {
        id
      }
    }
  }
`;

const DELETE_SCHEDULED_TRANSFER = gql`
  mutation($scheduledTransferId: ObjectID!) {
  cancelScheduledTransfer(scheduledTransferId: $scheduledTransferId) {
    scheduledTransfer {
      id
    }
  }
}
`;

const DELETE_SUB_ACCOUNT = gql`
  mutation closeSubAccount($input: CloseSubAccountInput!) {
    closeSubAccount(input: $input) {
      subAccount {
        id
      }
    }
  }
`;
const DELETE_ACCOUNT = gql`
  mutation closeAccount($input: CloseAccountInput!) {
    closeAccount(input: $input) {
      account {
        id
      }
    }
  }
`;
const DELETE_GOAL = gql`
  mutation archiveGoal($input: ArchiveGoalInput!) {
    archiveGoal(input: $input) {
      goal {
        id
      }
    }
  }
`;

const GET_MANUAL_PROCESS_TRANSFERS = gql`
  query ($fetchManualProcessId: ID!) {
    fetchManualProcess(id: $fetchManualProcessId) {
      manualProcess {
        subAccount {
          transfers {
            id
            sourceId
          }
        }
      }
    }
  }
`;

export const useWorkflowCleanup = () => {
  const [deleteSubAccount] = useMutation(DELETE_SUB_ACCOUNT);
  const [deleteAccount] = useMutation(DELETE_ACCOUNT);
  const [deleteGoal] = useMutation(DELETE_GOAL);
  const [deleteTransfers] = useMutation(DELETE_TRANSFERS);
  const [deleteScheduledTransfers] = useMutation(DELETE_SCHEDULED_TRANSFER);
  const [cancelManualProcess] = useMutation(CANCEL_MANUAL_PROCESS);
  const [cancelCustodianDepositTransfer] = useMutation(CANCEL_CUSTODIAN_DEPOSIT_TRANSFER);

  const [getManualProcessTransfers] = useLazyQuery<{
    fetchManualProcess: { manualProcess: { subAccount: { transfers: { id: string; sourceId: string }[] } } }
  }>(GET_MANUAL_PROCESS_TRANSFERS);

  const findTransferByManualProcessId = async (manualProcessId: string, maxRetries = 3): Promise<
  NonNullable<
  Awaited<ReturnType<typeof getManualProcessTransfers>>['data']
  >['fetchManualProcess']['manualProcess']['subAccount']['transfers'][number]
  > => {
    const result = await getManualProcessTransfers({
      variables: {
        fetchManualProcessId: manualProcessId,
      },
    });
    const transfer = result.data?.fetchManualProcess.manualProcess.subAccount.transfers.find((t) => t.sourceId === manualProcessId);
    if (!transfer) {
      if (maxRetries > 0) {
        await new Promise((resolve) => { setTimeout(resolve, 500); });
        return findTransferByManualProcessId(manualProcessId, maxRetries - 1);
      }
      throw new Error('No matching transfer found');
    }
    return transfer;
  };

  const cleanupWorkflow = async (workflowData: WorkflowContextType['workflowData']) => {
    const {
      createdAccounts = [],
      createdGoalIds = [],
      createdSubAccountIds = [],
      createdTransfers = [],
      createdScheduledTransfers = [],
      createdExternalTransfers = [],
      createdCustodianDepositTransfers = [],
    } = workflowData;

    await Promise.all([
      ...uniqBy(createdTransfers, 'id').map((t) => deleteTransfers({
        variables: {
          input: {
            transferId: t.id,
            transition: 'cancel',
            cancelReason: 'OTHER',
          },
        },
      })),
      ...uniqBy(createdScheduledTransfers, 'id').map((t) => deleteScheduledTransfers({
        variables: {
          scheduledTransferId: t.id,
        },
      })),
      ...uniqBy(createdExternalTransfers, 'id').map(async (p) => {
        const associatedTransfer = await findTransferByManualProcessId(p.id);
        await Promise.all([
          deleteTransfers({
            variables: {
              input: {
                transferId: associatedTransfer.id,
                transition: 'cancel',
                cancelReason: 'OTHER',
              },
            },
          }),
          cancelManualProcess({
            variables: {
              input: {
                manualProcessId: p.id,
                transition: 'cancel',
              },
            },
          }),
        ]);
      }),
      ...uniqBy(createdCustodianDepositTransfers, 'id').map((t) => cancelCustodianDepositTransfer({
        variables: {
          input: omit(t, 'confirmationNumber'),
        },
      })),
    ]);

    await Promise.all(uniq(createdSubAccountIds).map((id) => deleteSubAccount({
      variables: {
        input: {
          subAccountId: id,
          inactiveReason: 'OTHER',
        },
      },
    })));

    await Promise.all(uniqBy(createdAccounts, 'id').map((account) => deleteAccount({
      variables: {
        input: {
          accountId: account.id,
          inactiveReason: 'OTHER',
        },
      },
    })));

    await Promise.all(
      uniq(createdGoalIds).map((id) => deleteGoal({
        variables: {
          input: {
            goalId: id,
          },
        },
      })),
    );
  };

  return { cleanupWorkflow };
};
